import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import packageJson from "../../../package.json";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";

const languageOptions = [
    { value: "pt-BR", label: "BR", icon: BRFlag },
    { value: "en", label: "EN", icon: USFlag },
    { value: "es", label: "ES", icon: ESFlag },
    { value: "ar", label: "AR", icon: ARFlag },
];

// Marca MFlux (ícone de nós conectados) — inline p/ ficar igual ao mockup
const MFluxMark = ({ size = 33 }) => (
    <svg viewBox="0 0 24 24" fill="#34528C" width={size} height={size}>
        <rect x="2.5" y="3.5" width="7" height="5.2" rx="1.7" />
        <rect x="14.5" y="3.5" width="7" height="5.2" rx="1.7" />
        <rect x="9.2" y="14.6" width="7" height="5.2" rx="1.7" />
        <path d="M9 6.1 H15" stroke="#34528C" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path d="M5.4 8.7 L12.7 14.6" stroke="#34528C" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    </svg>
);

const useStyles = makeStyles((theme) => {
    const light = theme.mode !== "dark";
    // Paleta do form-side (claro/escuro), espelhando o mflux.css do mockup
    const surface = light ? "#ffffff" : "#111c33";
    const surface2 = light ? "#f8fafc" : "#16223c";
    const border = light ? "#e4e9f2" : "#243454";
    const text = light ? "#1a2540" : "#e8eef9";
    const text2 = light ? "#64748b" : "#9fb0cd";
    const text3 = light ? "#94a3b8" : "#6b7fa3";
    const brand = "#34528C";
    const brandLink = light ? "#34528C" : "#7a9fe0";
    const brandSoft = light ? "rgba(52,82,140,0.15)" : "rgba(122,159,224,0.20)";

    return {
        root: {
            width: "100vw",
            height: "100vh",
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            background: light ? "#eef2f8" : "#0a1220",
            padding: "16px",
            boxSizing: "border-box",
        },

        wrap: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            width: "min(1040px, 94vw)",
            height: "min(630px, 92vh)",
            borderRadius: "24px",
            overflow: "hidden",
            background: surface,
            border: `1px solid ${border}`,
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
            [theme.breakpoints.down("sm")]: {
                gridTemplateColumns: "1fr",
                height: "auto",
                maxWidth: "440px",
            },
        },

        // LADO ESQUERDO — marca (navy)
        brandSide: {
            position: "relative",
            background: "linear-gradient(150deg, #34528C, #0e1830 82%)",
            padding: "46px",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            "&::before": {
                content: '""',
                position: "absolute",
                width: "420px",
                height: "420px",
                borderRadius: "50%",
                background: "radial-gradient(circle, #5074b355, transparent 70%)",
                top: "-120px",
                right: "-120px",
            },
            "&::after": {
                content: '""',
                position: "absolute",
                width: "300px",
                height: "300px",
                borderRadius: "50%",
                background: "radial-gradient(circle, #8b5cf644, transparent 70%)",
                bottom: "-80px",
                left: "-60px",
            },
            [theme.breakpoints.down("sm")]: {
                display: "none",
            },
        },

        bsLogo: {
            display: "flex",
            alignItems: "center",
            gap: "13px",
            position: "relative",
            zIndex: 2,
        },
        bsLogoMark: {
            width: "50px",
            height: "50px",
            borderRadius: "13px",
            background: "#fff",
            display: "grid",
            placeItems: "center",
        },
        bsName: {
            fontSize: "27px",
            fontWeight: 800,
            letterSpacing: "-.5px",
            "& span": {
                color: "#a9c1ec",
                fontWeight: 500,
            },
        },
        brandTitle: {
            fontSize: "33px",
            lineHeight: 1.18,
            margin: "auto 0 16px",
            fontWeight: 800,
            letterSpacing: "-1px",
            position: "relative",
            zIndex: 2,
            maxWidth: "380px",
        },
        brandText: {
            color: "#c7d6f0",
            fontSize: "15px",
            maxWidth: "330px",
            position: "relative",
            zIndex: 2,
            margin: "0 0 28px",
        },
        feat: {
            display: "flex",
            flexDirection: "column",
            gap: "13px",
            position: "relative",
            zIndex: 2,
        },
        featItem: {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            color: "#dbe6fa",
            fontSize: "14px",
            fontWeight: 500,
            "& > span": {
                width: "24px",
                height: "24px",
                borderRadius: "7px",
                background: "#ffffff1f",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
            },
        },

        // LADO DIREITO — formulário
        formSide: {
            position: "relative",
            padding: "54px 50px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            background: surface,
            [theme.breakpoints.down("sm")]: {
                padding: "40px 28px",
            },
        },
        welcome: {
            fontSize: "25px",
            fontWeight: 800,
            letterSpacing: "-.5px",
            margin: "0 0 4px",
            color: text,
        },
        sub: {
            color: text2,
            margin: "0 0 28px",
            fontSize: "14px",
        },

        field: { marginBottom: "17px" },
        fieldLabel: {
            display: "block",
            fontSize: "13px",
            fontWeight: 600,
            color: text2,
            marginBottom: "7px",
        },
        inp: {
            display: "flex",
            alignItems: "center",
            gap: "10px",
            border: `1.5px solid ${border}`,
            borderRadius: "11px",
            padding: "12px 14px",
            transition: ".15s",
            background: surface2,
            "&:focus-within": {
                borderColor: brand,
                boxShadow: `0 0 0 4px ${brandSoft}`,
            },
            "& > svg": {
                width: "18px",
                height: "18px",
                color: text3,
                flexShrink: 0,
            },
        },
        input: {
            border: 0,
            outline: 0,
            background: "transparent",
            font: "inherit",
            width: "100%",
            color: text,
            "&::placeholder": { color: text3 },
        },
        eye: {
            cursor: "pointer",
            color: text3,
            flexShrink: 0,
        },

        rowBetween: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "6px 0 22px",
            fontSize: "13px",
            flexWrap: "wrap",
            gap: "8px",
        },
        remember: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: text2,
            cursor: "pointer",
            userSelect: "none",
            "& input": { accentColor: brand, cursor: "pointer" },
        },
        link: {
            color: brandLink,
            fontWeight: 600,
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
        },

        loginBtn: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "13px",
            fontSize: "15px",
            fontWeight: 700,
            color: "#fff",
            background: "linear-gradient(90deg, #34528C, #2b4479)",
            border: "none",
            borderRadius: "11px",
            cursor: "pointer",
            fontFamily: "inherit",
            boxShadow: "0 6px 18px rgba(52,82,140,0.35)",
            transition: "all .2s ease",
            "&:hover": {
                opacity: 0.94,
                transform: "translateY(-1px)",
                boxShadow: "0 8px 22px rgba(52,82,140,0.45)",
            },
        },

        signup: {
            textAlign: "center",
            color: text2,
            fontSize: "14px",
            marginTop: "24px",
            "& a, & span.act": {
                color: brandLink,
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
            },
        },

        version: {
            textAlign: "center",
            fontSize: "11px",
            color: text3,
            marginTop: "18px",
        },

        // Seletor de idioma (canto sup. direito do form)
        langWrap: {
            position: "absolute",
            top: "20px",
            right: "24px",
            zIndex: 10,
        },
        langBtn: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            background: "transparent",
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "6px 10px",
            fontSize: "13px",
            fontWeight: 600,
            color: text2,
            "&:hover": { borderColor: brand },
        },
        langDropdown: {
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "6px",
            background: surface,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            borderRadius: "10px",
            border: `1px solid ${border}`,
            padding: "6px",
            minWidth: "130px",
        },
        langOption: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "7px 10px",
            border: "none",
            background: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: 500,
            color: text2,
            cursor: "pointer",
            textAlign: "left",
            "&:hover": { background: surface2 },
        },
        flagIcon: {
            width: 18,
            height: 13,
            borderRadius: 2,
            objectFit: "cover",
        },
    };
});

const Login = () => {
    const classes = useStyles();
    const history = useHistory();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);

    const [langOpen, setLangOpen] = useState(false);
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
    const langRef = useRef();

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) {
                setLangOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => setAllowSignup(data === "enabled"))
            .catch(() => {});

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try { if (langs) arr = JSON.parse(langs); } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => setEnabledLanguages(["pt-BR", "en"]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        handleLogin(user);
    };

    const currentLang = languageOptions.find((o) => o.value === i18n.language) || languageOptions[0];

    const handleSelectLang = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setLangOpen(false);
        window.location.reload();
    };

    return (
        <>
            <Helmet>
                <title>{appName || "MFlux"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <div className={classes.root}>
                <div className={classes.wrap}>
                    {/* LADO ESQUERDO — MARCA */}
                    <div className={classes.brandSide}>
                        <div className={classes.bsLogo}>
                            <div className={classes.bsLogoMark}>
                                <MFluxMark size={33} />
                            </div>
                            <div className={classes.bsName}>M<span>Flux</span></div>
                        </div>
                        <h1 className={classes.brandTitle}>
                            Sua central de atendimento, em fluxo.
                        </h1>
                        <p className={classes.brandText}>
                            Gerencie conversas, automações e equipes em um só lugar —
                            rápido, inteligente e do seu jeito.
                        </p>
                        <div className={classes.feat}>
                            <div className={classes.featItem}><span>✓</span> Atendimento multicanal unificado</div>
                            <div className={classes.featItem}><span>✓</span> Fluxos e chatbots sem código</div>
                            <div className={classes.featItem}><span>✓</span> Relatórios e métricas em tempo real</div>
                        </div>
                    </div>

                    {/* LADO DIREITO — FORMULÁRIO */}
                    <div className={classes.formSide}>
                        {/* Seletor de idioma */}
                        <div className={classes.langWrap} ref={langRef}>
                            <button className={classes.langBtn} onClick={() => setLangOpen((o) => !o)}>
                                <img src={currentLang.icon} alt={currentLang.label} className={classes.flagIcon} />
                                {currentLang.label}
                                <span style={{ fontSize: 10 }}>▾</span>
                            </button>
                            {langOpen && (
                                <div className={classes.langDropdown}>
                                    {languageOptions
                                        .filter((o) => enabledLanguages.includes(o.value))
                                        .map((opt) => (
                                            <button
                                                key={opt.value}
                                                className={classes.langOption}
                                                onClick={() => handleSelectLang(opt)}
                                            >
                                                <img src={opt.icon} alt={opt.label} className={classes.flagIcon} />
                                                {opt.label}
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>

                        <h2 className={classes.welcome}>Bem-vindo de volta 👋</h2>
                        <p className={classes.sub}>Entre com suas credenciais para acessar o painel.</p>

                        <form noValidate onSubmit={handlSubmit}>
                            <div className={classes.field}>
                                <label className={classes.fieldLabel}>
                                    {i18n.t("login.form.email") || "E-mail"}
                                </label>
                                <div className={classes.inp}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="4" width="20" height="16" rx="2" />
                                        <path d="m22 6-10 7L2 6" />
                                    </svg>
                                    <input
                                        className={classes.input}
                                        type="email"
                                        name="email"
                                        placeholder="seu@email.com"
                                        value={user.email}
                                        onChange={handleChangeInput}
                                        autoComplete="email"
                                        autoFocus
                                        required
                                    />
                                </div>
                            </div>

                            <div className={classes.field}>
                                <label className={classes.fieldLabel}>
                                    {i18n.t("login.form.password") || "Senha"}
                                </label>
                                <div className={classes.inp}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    <input
                                        className={classes.input}
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder="••••••"
                                        value={user.password}
                                        onChange={handleChangeInput}
                                        autoComplete="current-password"
                                        required
                                    />
                                    {showPassword ? (
                                        <svg className={classes.eye} onClick={() => setShowPassword(false)}
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
                                        </svg>
                                    ) : (
                                        <svg className={classes.eye} onClick={() => setShowPassword(true)}
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </div>
                            </div>

                            <div className={classes.rowBetween}>
                                <label className={classes.remember}>
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    Manter conectado
                                </label>
                                <span
                                    className={classes.link}
                                    onClick={() => history.push("/forgot-password")}
                                >
                                    Esqueci a senha
                                </span>
                            </div>

                            <button type="submit" className={classes.loginBtn}>
                                Entrar no MFlux
                            </button>
                        </form>

                        <p className={classes.signup}>
                            {allowSignup ? (
                                <>
                                    Não tem conta?{" "}
                                    <span className="act" onClick={() => history.push("/signup")}>
                                        Cadastre-se
                                    </span>
                                </>
                            ) : (
                                <>Não tem conta? <span className="act">Fale com seu gestor</span></>
                            )}
                        </p>

                        <div className={classes.version}>
                            © {new Date().getFullYear()} {appName || "MFlux"} · v{packageJson.version}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
